import { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import Header from '../../components/feature/Header';
import LocalImageUploader from '../../components/feature/LocalImageUploader';
import StockDisplay from '../../components/feature/StockDisplay';
import { getProductImage } from '../../utils/imageStorage';

export default function ProduitAngeJoyeuxCoeur() {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [stockInfo, setStockInfo] = useState<{ available: boolean } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const defaultImage = 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/9ca0f2dee5922ad907a452879600467c.png';
  
  const [productImage, setProductImage] = useState(defaultImage);

  useEffect(() => {
    loadProductImage();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = () => {
    const adminKey = localStorage.getItem('adminKey');
    setIsAdmin(adminKey === 'lolotte2024');
  };

  const loadProductImage = async () => {
    const savedImage = await getProductImage('ange-joyeux-coeur');
    if (savedImage) {
      setProductImage(savedImage);
    }
  };

  const product = {
    id: 'ange-joyeux-coeur',
    name: 'Ange "Joyeux Cœur"',
    price: 4,
    image: productImage,
    description: 'Le petit ange au sourire radieux ✨\nRéalisé à partir de pages de livre pliées à la main, cet ange illumine les cœurs avec son visage joyeux.\nSa ficelle naturelle lui permet de s\'accrocher partout : au sapin, à une poignée de porte ou à un paquet cadeau.',
    details: {
      fabrication: 'Fait main dans l\'atelier Les Carnets de Lolotte',
      type: 'Pièce artisanale unique',
      dimensions: '8 cm (hors ficelle)',
      delai: '2 à 3 jours',
      materials: 'Pages de livre recyclées, ficelle naturelle'
    }
  };

  const handleAddToCart = () => {
    if (!stockInfo?.available) return;
    
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      description: 'Fait main à partir de livres anciens',
      link: '/produit-ange-joyeux-coeur',
    }, quantity);
    
    // Feedback visuel
    const button = document.activeElement as HTMLButtonElement;
    if (button) {
      const originalText = button.innerHTML;
      button.innerHTML = '<i class="ri-check-line mr-2"></i>Ajouté au panier !';
      button.disabled = true;
      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image du produit */}
            <div>
              <div className="sticky top-24">
                <img
                  src={productImage}
                  alt="Ange Joyeux Cœur"
                  className="w-full h-auto rounded-lg shadow-lg object-cover"
                />
                
                {/* Bouton pour afficher l'uploader - UNIQUEMENT POUR L'ADMIN */}
                {isAdmin && (
                  <>
                    <button
                      onClick={() => setShowImageUploader(!showImageUploader)}
                      className="mt-4 w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
                    >
                      {showImageUploader ? '✕ Fermer' : '📸 Modifier l\'image'}
                    </button>

                    {/* Module de changement d'image */}
                    {showImageUploader && (
                      <div className="mt-4">
                        <LocalImageUploader
                          productId="ange-joyeux-coeur"
                          currentImage={defaultImage}
                          onImageChange={setProductImage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Informations produit */}
            <div className="space-y-8">
              
              {/* Titre et prix */}
              <div>
                <h1 className="text-4xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  {product.name}
                </h1>
                <div className="flex items-center space-x-4 mb-6">
                  <span className="text-3xl font-bold text-[#C49B86]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.price}€
                  </span>
                </div>
                
                {/* Affichage du stock */}
                <StockDisplay 
                  productId={product.id}
                  className="mb-4"
                />
              </div>

              {/* Description */}
              <div className="space-y-4">
                {product.description.split('\n').map((line, index) => (
                  <p key={index} className="text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {line}
                  </p>
                ))}
              </div>

              {/* Détails techniques */}
              <div className="bg-[#FAF9F8] p-6 rounded-lg border border-[#C49B86]">
                <h3 className="text-xl font-semibold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Détails du produit
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Fabrication :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.fabrication}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Type :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Dimensions :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.dimensions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Délai :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.delai}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Matériaux :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.materials}</span>
                  </div>
                </div>
              </div>

              {/* Sélection quantité et achat */}
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <label className="text-lg font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Quantité :
                  </label>
                  <div className="flex items-center border-2 border-[#C49B86] rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-[#C49B86] hover:bg-[#F8EDEA] transition-colors"
                      disabled={quantity <= 1}
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="px-4 py-2 text-lg font-medium text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-3 py-2 text-[#C49B86] hover:bg-[#F8EDEA] transition-colors"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <span className="text-lg text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Total : <span className="font-bold text-[#C49B86]">{(product.price * quantity).toFixed(2)}€</span>
                  </span>
                </div>

                {/* Bouton d'achat */}
                <button
                  onClick={handleAddToCart}
                  disabled={!stockInfo?.available}
                  className={`w-full px-8 py-4 rounded-full font-semibold text-lg border-2 transition-colors whitespace-nowrap ${
                    !stockInfo?.available 
                      ? 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed'
                      : 'bg-[#F8EDEA] text-[#B97A72] border-[#C49B86] hover:bg-[#C49B86] hover:text-white cursor-pointer'
                  }`}
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  {!stockInfo?.available ? 'Rupture de stock' : 'Ajouter au panier'}
                </button>

                {/* Phrase poétique */}
                <div className="text-center pt-4">
                  <p className="text-lg text-[#C49B86] italic font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                    🌸 <em>Parce qu'un sourire d'ange illumine plus qu'une guirlande...</em>
                  </p>
                </div>
              </div>

              {/* Informations de livraison */}
              <div className="bg-[#EBD2CF] p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <i className="ri-truck-line text-[#B97A72] text-xl"></i>
                  <span className="font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Livraison soignée
                  </span>
                </div>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Votre ange sera emballé avec soin et expédié sous 2 à 3 jours ouvrés.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
